/**
 * @file ProTeamSample.jsx
 * @description The third "What Pro adds" screen on the agency page: a drawn sample of the Team screen (admin,
 *   a recruiter, a clinical director, an Invite button) in the same browser frame as the two Pro screenshots.
 *   Sample names and times only, no real data; the frame carries the Sample tag. Nothing here is interactive
 *   (the frame wraps it in role="img"). Seat count comes from the caller (lib/agencyPricing teamLogins), labels
 *   reuse the Team screen's own keys (team.section.*) so the sample and the product say the same words.
 * @module components/ProTeamSample
 */
import { useTranslation } from "react-i18next";

const NAVY = "#0F1024";
const MID = "#475569";
const LIGHT = "#64748B";
const LINE = "#E2E8F0";

/** The sample team, in seat order. The first is the owner (Admin); `pending` shows an invite that has not been accepted yet. */
const SAMPLE_SEATS = [
  { id: "owner", name: "Jordan M.", tone: { bg: "#E0E7FF", fg: "#3730A3" } },
  { id: "recruiter", name: "Dana R.", roleKey: "agencyLanding.teamSample.recruiter", tone: { bg: "#D1FAE5", fg: "#065F46" } },
  { id: "clinical", name: "Marcus T.", roleKey: "agencyLanding.teamSample.clinicalDirector", tone: { bg: "#D1FAE5", fg: "#065F46" }, pending: true },
];

const initials = (name) => name.split(/\s+/).map(w => w[0]).join("").slice(0, 2).toUpperCase();

/** @param {{ limit: number, isMobile?: boolean }} props limit = logins Pro includes, the owner counted */
export default function ProTeamSample({ limit, isMobile = false }) {
  const { t } = useTranslation();
  const seats = SAMPLE_SEATS.slice(0, Math.max(1, Math.min(limit, SAMPLE_SEATS.length)));
  const hasOpenSeat = limit > seats.length;
  const used = seats.length;
  const size = isMobile ? 34 : 50;

  /** Second line: the role, then when they were last here. An unaccepted invite has no activity yet. */
  const detail = (seat) => {
    const role = seat.roleKey ? t(seat.roleKey) : null;
    const when = seat.id === "owner" ? t("team.section.activeNow")
      : seat.pending ? null
      : t("team.section.lastActive", { ago: t("agencyLanding.teamSample.ago") });
    return [role, when].filter(Boolean).join(" · ");
  };

  return (
    <div data-testid="pro-team-sample" style={{ height: "100%", boxSizing: "border-box", display: "flex", alignItems: isMobile ? "flex-start" : "center", justifyContent: "center", padding: isMobile ? "14px 12px 34px" : "24px 24px 40px" }}>
      <div style={{ width: "100%", maxWidth: 680, background: "#fff", border: `1px solid ${LINE}`, borderRadius: 14, boxShadow: "0 1px 3px rgba(15,23,42,0.06)", padding: isMobile ? "12px 12px 4px" : "24px 28px 12px", fontFamily: "'Inter', sans-serif" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, paddingBottom: isMobile ? 10 : 18, borderBottom: `1px solid ${LINE}` }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: isMobile ? 16 : 22, fontWeight: 800, color: NAVY, lineHeight: 1.2 }}>{t("team.section.title")}</div>
            <div style={{ fontSize: isMobile ? 12 : 14, color: LIGHT, marginTop: 3 }}>{t("team.section.seats", { used, limit })}</div>
          </div>
          <span style={{ flex: "none", padding: isMobile ? "7px 14px" : "11px 24px", borderRadius: 10, background: "#4F46E5", color: "#fff", fontSize: isMobile ? 12.5 : 15, fontWeight: 700 }}>
            {t("agencyLanding.teamSample.invite")}
          </span>
        </div>

        {seats.map((seat, i) => (
          <div key={seat.id} style={{ display: "flex", alignItems: "center", gap: isMobile ? 10 : 14, padding: isMobile ? "9px 0" : "18px 0", borderTop: i ? `1px solid ${LINE}` : "none" }}>
            <span aria-hidden="true" style={{ width: size, height: size, borderRadius: "50%", flex: "none", display: "inline-flex", alignItems: "center", justifyContent: "center", background: seat.tone.bg, color: seat.tone.fg, fontSize: isMobile ? 12 : 16, fontWeight: 800 }}>{initials(seat.name)}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: isMobile ? 13.5 : 17, fontWeight: 700, color: NAVY, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {seat.name}{seat.id === "owner" && <span style={{ fontWeight: 500, color: LIGHT }}> ({t("team.section.you")})</span>}
              </div>
              <div style={{ fontSize: isMobile ? 12 : 14, color: MID, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {detail(seat)}
              </div>
            </div>
            <span style={{ flex: "none", fontSize: 12, fontWeight: 700, padding: isMobile ? "3px 9px" : "4px 12px", borderRadius: 99, background: seat.pending ? "#FEF3C7" : seat.id === "owner" ? "#E0E7FF" : "#D1FAE5", color: seat.pending ? "#92400E" : seat.id === "owner" ? "#3730A3" : "#065F46" }}>
              {seat.id === "owner" ? t("team.section.owner") : seat.pending ? t("team.section.pending") : t("team.section.member")}
            </span>
          </div>
        ))}

        {hasOpenSeat && (
          <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 10 : 14, padding: isMobile ? "9px 0" : "18px 0", borderTop: `1px solid ${LINE}` }}>
            <span aria-hidden="true" style={{ width: size, height: size, borderRadius: "50%", flex: "none", border: "1.5px dashed #94A3B8", boxSizing: "border-box" }} />
            <span style={{ fontSize: isMobile ? 13.5 : 15, color: LIGHT, fontWeight: 600 }}>{t("team.section.openSeat")}</span>
          </div>
        )}
      </div>
    </div>
  );
}
